import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { JsonLd } from '@/components/JsonLd';
import { PageMeta, SITE_URL } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { getArticleBySlugApi, getPublishedArticlesApi } from '@/lib/api/blogApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { formatDateAbbreviatedFr, getInitials } from '@shared';
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { ArrowRight, BookOpen, Calendar, ChevronRight, Clock, Copy, Share2, Tag } from 'lucide-react';

import { Facebook, Linkedin, Twitter } from '../components/ui/social-icons';

function getCoverUrl(files?: Array<{ id: number; key: string; usage: string }>): string | null {
  const cover = files?.find(f => f.usage === 'ARTICLE_COVER');
  if (!cover) return null;
  const imagesUrl = import.meta.env.VITE_IMAGES_URL as string | undefined;
  if (imagesUrl) return `${imagesUrl}/${cover.key.replace('./uploads/', '')}`;
  const apiUrl = import.meta.env.VITE_URL_API as string;
  return `${apiUrl}/v1/files/${cover.id}/serve`;
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const {
    data: article,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.blog.articleBySlug(slug!),
    queryFn: () => getArticleBySlugApi(slug!),
    enabled: !!slug,
  });

  // Related articles (latest 3, excluding current)
  const { data: relatedData } = useQuery({
    queryKey: queryKeys.blog.articles({ limit: 4 }),
    queryFn: () => getPublishedArticlesApi({ limit: 4 }),
  });

  const relatedArticles = (relatedData?.data ?? []).filter(a => a.slug !== slug).slice(0, 3);

  // Le HTML TipTap vient d'admins authentifiés mais n'est pas sanitisé côté API :
  // DOMPurify neutralise scripts/handlers avant le dangerouslySetInnerHTML.
  const articleContent = article?.content;
  const sanitizedContent = useMemo(() => (articleContent ? DOMPurify.sanitize(articleContent) : ''), [articleContent]);

  if (isLoading) {
    return (
      <div className='flex justify-center py-40'>
        <div className='h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent' />
      </div>
    );
  }

  if (isError || !article) {
    return (
      <div className='pt-40 pb-20 px-6 text-center'>
        {/* URL dynamique non publiable (brouillon, slug inconnu…) : noindex pour
            éviter le duplicate content côté crawler. */}
        <PageMeta
          title='Article introuvable'
          description="Cet article n'existe pas ou n'est pas publié."
          robots='noindex, nofollow'
        />
        <h1 className='text-3xl font-extrabold text-slate-900 mb-4'>Article introuvable</h1>
        <p className='text-slate-500 mb-8'>Cet article n&apos;existe pas ou a ete supprime.</p>
        <Link
          to={ROUTES.BLOG}
          className='inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-2xl font-bold text-sm hover:bg-blue-700 transition-colors'
        >
          Retour au blog
        </Link>
      </div>
    );
  }

  const authorName = article.author
    ? `${article.author.firstName ?? ''} ${article.author.lastName ?? ''}`.trim() || article.author.username
    : 'Auteur';
  const authorInitials = article.author
    ? getInitials(article.author.firstName ?? '', article.author.lastName ?? '')
    : '?';
  const publishDate = formatDateAbbreviatedFr(article.publishedAt ?? article.createdAt);

  // Fil d'Ariane : un article rattache a un club ou a un organisateur pointe
  // aussi vers le blog de l'entite (/clubs/:id/blog, /organisateurs/:id/blog).
  const entityBlog = article.organization
    ? {
        to: ROUTES.ORGANIZATION_BLOG.replace(':id', String(article.organization.id)),
        label: `Blog de ${article.organization.name}`,
      }
    : article.club
      ? {
          to: ROUTES.CLUB_BLOG.replace(':id', String(article.club.id)),
          label: `Blog de ${article.club.name}`,
        }
      : null;

  function handleCopyLink() {
    void navigator.clipboard.writeText(window.location.href);
  }

  return (
    <>
      <PageMeta
        title={article.title}
        description={article.excerpt}
        image={getCoverUrl(article.files) ?? undefined}
        ogType='article'
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          url: `${SITE_URL}/blog/${slug}`,
          mainEntityOfPage: `${SITE_URL}/blog/${slug}`,
          datePublished: article.publishedAt ?? article.createdAt,
          dateModified: article.publishedAt ?? article.createdAt,
          author: { '@type': 'Person', name: authorName },
          publisher: {
            '@type': 'Organization',
            name: 'BoulesConnect',
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/apple-icon.png` },
          },
          ...(getCoverUrl(article.files) ? { image: getCoverUrl(article.files) } : {}),
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
            ...(entityBlog
              ? [{ '@type': 'ListItem', position: 3, name: entityBlog.label, item: `${SITE_URL}${entityBlog.to}` }]
              : []),
            {
              '@type': 'ListItem',
              position: entityBlog ? 4 : 3,
              name: article.title,
              item: `${SITE_URL}/blog/${slug}`,
            },
          ],
        }}
      />

      <div className='bg-[#F8FAFC] text-slate-900 font-sans'>
        <article className='pt-40 pb-20 px-6'>
          <div className='max-w-4xl mx-auto'>
            {/* Breadcrumb */}
            <div className='flex items-center gap-2 text-sm text-slate-500 mb-8'>
              <Link to={ROUTES.BLOG} className='hover:text-blue-600 transition-colors'>
                Blog
              </Link>
              <ChevronRight className='w-4 h-4' />
              {entityBlog && (
                <>
                  <Link to={entityBlog.to} className='hover:text-blue-600 transition-colors line-clamp-1'>
                    {entityBlog.label}
                  </Link>
                  <ChevronRight className='w-4 h-4' />
                </>
              )}
              {article.category && (
                <>
                  <span className='text-slate-400'>{article.category.name}</span>
                  <ChevronRight className='w-4 h-4' />
                </>
              )}
              <span className='text-slate-900 font-medium line-clamp-1'>{article.title}</span>
            </div>

            {/* Header */}
            <header className='mb-12'>
              {article.category && (
                <span className='inline-flex items-center gap-1.5 bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full text-xs font-bold mb-6'>
                  <Tag className='w-3.5 h-3.5' /> {article.category.name}
                </span>
              )}

              <h1 className='text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-6'>{article.title}</h1>

              <p className='text-xl text-slate-500 leading-relaxed mb-8'>{article.excerpt}</p>

              {/* Author bar */}
              <div className='flex items-center gap-4 pb-8 border-b border-slate-200'>
                <div className='w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold'>
                  {authorInitials}
                </div>
                <div className='flex-1'>
                  <div className='font-bold text-slate-900'>{authorName}</div>
                  <div className='flex items-center gap-4 text-sm text-slate-500'>
                    <span className='flex items-center gap-1'>
                      <Calendar className='w-3.5 h-3.5' /> {publishDate}
                    </span>
                    {article.readTimeMinutes && (
                      <span className='flex items-center gap-1'>
                        <Clock className='w-3.5 h-3.5' /> {article.readTimeMinutes} min de lecture
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Hero image */}
              {(() => {
                const coverUrl = getCoverUrl(article.files);
                return coverUrl ? (
                  <div className='mt-8 aspect-video overflow-hidden rounded-3xl'>
                    <img src={coverUrl} alt={article.title} className='h-full w-full object-cover' />
                  </div>
                ) : (
                  <div className='mt-8 aspect-video bg-gradient-to-br from-blue-200 via-purple-200 to-pink-200 rounded-3xl flex items-center justify-center'>
                    <div className='text-center'>
                      <BookOpen className='w-12 h-12 text-slate-400 mx-auto mb-2' />
                      <span className='text-slate-400 text-sm'>Image d&apos;illustration</span>
                    </div>
                  </div>
                );
              })()}
            </header>

            {/* Article Content (HTML from TipTap) */}
            <div
              className='prose prose-slate prose-lg max-w-none'
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className='flex flex-wrap items-center gap-2 mt-12 pt-8 border-t border-slate-200'>
                <Tag className='w-4 h-4 text-slate-400' />
                {article.tags.map(tag => (
                  <Link
                    key={tag}
                    to={`${ROUTES.BLOG}?tag=${encodeURIComponent(tag)}`}
                    className='px-3 py-1.5 bg-slate-100 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors'
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Share bar */}
            <div className='flex items-center justify-between mt-8 p-6 bg-white rounded-2xl border border-slate-200'>
              <div className='flex items-center gap-2 text-slate-500'>
                <Share2 className='w-5 h-5' />
                <span className='font-semibold text-sm'>Partager cet article</span>
              </div>
              <div className='flex items-center gap-3'>
                {[
                  { icon: Facebook, color: 'bg-blue-100 text-blue-600 hover:bg-blue-200' },
                  { icon: Twitter, color: 'bg-sky-100 text-sky-600 hover:bg-sky-200' },
                  { icon: Linkedin, color: 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200' },
                ].map((social, i) => (
                  <button
                    key={i}
                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${social.color}`}
                  >
                    <social.icon className='w-5 h-5' />
                  </button>
                ))}
                <button
                  onClick={handleCopyLink}
                  className='w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors'
                  title='Copier le lien'
                >
                  <Copy className='w-5 h-5' />
                </button>
              </div>
            </div>

            {/* CTA */}
            <div className='mt-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-10 text-white text-center relative overflow-hidden'>
              <div className='absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2' />
              <div className='absolute bottom-0 left-0 w-36 h-36 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2' />
              <div className='relative z-10'>
                <h3 className='text-2xl font-extrabold mb-3'>Pret a passer a l&apos;action ?</h3>
                <p className='text-blue-100 mb-6 max-w-lg mx-auto'>
                  Inscrivez-vous a un concours pres de chez vous et mesurez-vous aux meilleurs joueurs de votre region.
                </p>
                <Link
                  to={ROUTES.SEARCH_CONCOURS}
                  className='inline-flex items-center gap-2 bg-white text-blue-700 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-blue-50 transition-colors hover-lift'
                >
                  Trouver un concours <ArrowRight className='w-5 h-5' />
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className='px-6 pb-20'>
            <div className='max-w-6xl mx-auto'>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-8'>Articles similaires</h2>
              <div className='grid md:grid-cols-3 gap-6'>
                {relatedArticles.map(related => (
                  <Link
                    key={related.id}
                    to={ROUTES.ARTICLE.replace(':slug', related.slug)}
                    className='bg-white rounded-3xl border border-slate-200 overflow-hidden hover-lift transition-all duration-300 group'
                  >
                    {(() => {
                      const coverUrl = getCoverUrl(related.files);
                      return coverUrl ? (
                        <div className='h-48 overflow-hidden'>
                          <img
                            src={coverUrl}
                            alt={related.title}
                            className='h-full w-full object-cover transition-transform group-hover:scale-105'
                          />
                        </div>
                      ) : (
                        <div className='h-48 bg-gradient-to-br from-blue-400 to-indigo-500 relative'>
                          <div className='absolute inset-0 opacity-20'>
                            <div className='absolute top-4 right-4 w-16 h-16 rounded-full bg-white/20' />
                          </div>
                        </div>
                      );
                    })()}
                    <div className='p-6'>
                      {related.category && (
                        <span className='text-xs font-bold text-purple-600 uppercase tracking-wider'>
                          {related.category.name}
                        </span>
                      )}
                      <h3 className='font-bold text-lg text-slate-900 mt-2 mb-3 group-hover:text-blue-600 transition-colors leading-snug'>
                        {related.title}
                      </h3>
                      <div className='flex items-center gap-4 text-sm text-slate-500'>
                        <span className='flex items-center gap-1'>
                          <Calendar className='w-3.5 h-3.5' />{' '}
                          {formatDateAbbreviatedFr(related.publishedAt ?? related.createdAt)}
                        </span>
                        {related.readTimeMinutes && (
                          <span className='flex items-center gap-1'>
                            <Clock className='w-3.5 h-3.5' /> {related.readTimeMinutes} min
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
